/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, November 9th 2021, 1:43:50 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */
import * as React from "react";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { UUID4 } from "lib/types";
import { SearchInputWidget } from "ui-widgets/search/SearchInputWidget";

interface TransferListItem {
  id: UUID4 | number;
  searchableName: string;
  renderedItem: React.ReactNode;
}

export interface TransferListProps {
  selectedItems: TransferListItem[]; // On the right
  SetSelectedItems: Function;
  unSelectedItems: TransferListItem[]; // On the left
  SetUnSelectedItems: Function;
  listWidth?: number;
  listHeight?: number;
  showSearch?: boolean;
}

const not = (a: readonly TransferListItem[], b: readonly TransferListItem[]) => {
  return a.filter((value) => b.map((v) => v.id).indexOf(value.id) === -1);
};

const intersection = (a: readonly TransferListItem[], b: readonly TransferListItem[]) => {
  return a.filter((value) => b.map((v) => v.id).indexOf(value.id) !== -1);
};

export const TransferList = ({
  selectedItems,
  unSelectedItems,
  SetSelectedItems,
  SetUnSelectedItems,
  listHeight,
  listWidth,
  showSearch,
}: TransferListProps) => {
  const [checkedItems, setCheckedItems] = React.useState<readonly TransferListItem[]>([]);

  const leftChecked = intersection(checkedItems, unSelectedItems);
  const rightChecked = intersection(checkedItems, selectedItems);

  const handleToggle = (value: TransferListItem) => () => {
    const currentIndex = checkedItems.map((v) => v.id).indexOf(value.id);
    const newChecked = [...checkedItems];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setCheckedItems(newChecked);
  };

  const handleAllRight = () => {
    SetSelectedItems(selectedItems.concat(unSelectedItems));
    SetUnSelectedItems([]);
  };

  const handleCheckedRight = () => {
    SetSelectedItems(selectedItems.concat(leftChecked));
    SetUnSelectedItems(not(unSelectedItems, leftChecked));
    setCheckedItems(not(checkedItems, leftChecked));
  };

  const handleCheckedLeft = () => {
    SetUnSelectedItems(unSelectedItems.concat(rightChecked));
    SetSelectedItems(not(selectedItems, rightChecked));
    setCheckedItems(not(checkedItems, rightChecked));
  };

  const handleAllLeft = () => {
    SetUnSelectedItems(unSelectedItems.concat(selectedItems));
    SetSelectedItems([]);
  };

  const [searchTerms, setSearchTerms] = React.useState<{
    selected: string;
    unSelected: string;
  }>();

  const customList = (items: readonly TransferListItem[], itemsType: "selected" | "unselected") => {
    let filteredItems = items;

    if (itemsType === "selected" && searchTerms?.selected) {
      filteredItems = items.filter((item) =>
        item.searchableName?.toLocaleLowerCase().includes(searchTerms?.selected?.toLocaleLowerCase())
      );
    } else if (itemsType === "unselected" && searchTerms?.unSelected) {
      filteredItems = items.filter((item) =>
        item.searchableName?.toLocaleLowerCase().includes(searchTerms?.unSelected?.toLocaleLowerCase())
      );
    }

    const handleSearch = (searchText: string) => {
      if (itemsType === "selected") {
        setSearchTerms((prev) => ({ ...prev, selected: searchText }));
      } else if (itemsType === "unselected") {
        setSearchTerms((prev) => ({ ...prev, unSelected: searchText }));
      }
    };

    return (
      <Paper sx={{ width: listWidth ?? 200, height: listHeight ?? 230, overflow: "auto" }}>
        <List dense component="div" role="list">
          {showSearch ? (
            <ListItem role="listitem">
              <SearchInputWidget extraSearchProps={{ handleSearch, debounceSearch: true }} />
            </ListItem>
          ) : null}
          {filteredItems.map((value: TransferListItem) => {
            const labelId = `transfer-list-item-${value}-label`;

            return (
              <div key={value.id}>
                <ListItem role="listitem" onClick={handleToggle(value)}>
                  <ListItemIcon>
                    <Checkbox
                      checked={checkedItems.some((checkedItem) => checkedItem.id === value.id)}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{
                        "aria-labelledby": labelId,
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText id={labelId} primary={value.renderedItem} />
                </ListItem>
                <Divider variant="middle" component="li" />
              </div>
            );
          })}
          <ListItem />
        </List>
      </Paper>
    );
  };

  return (
    <Grid container spacing={2} justifyContent="center" alignItems="center">
      <Grid item>{customList(unSelectedItems, "unselected")}</Grid>
      <Grid item>
        <Grid container direction="column" alignItems="center">
          <MoveButtonTooltip title="Add all">
            <Button
              sx={{ my: 0.5 }}
              variant="outlined"
              size="small"
              onClick={handleAllRight}
              disabled={unSelectedItems.length === 0 || checkedItems?.length > 0}
              aria-label="move all right"
            >
              ≫
            </Button>
          </MoveButtonTooltip>
          <MoveButtonTooltip title="Add selected only">
            <Button
              sx={{ my: 0.5 }}
              variant="outlined"
              size="small"
              onClick={handleCheckedRight}
              disabled={leftChecked.length === 0}
              aria-label="move selected right"
            >
              &gt;
            </Button>
          </MoveButtonTooltip>
          <MoveButtonTooltip title="Remove selected only">
            <Button
              sx={{ my: 0.5 }}
              variant="outlined"
              size="small"
              onClick={handleCheckedLeft}
              disabled={rightChecked.length === 0}
              aria-label="move selected left"
            >
              &lt;
            </Button>
          </MoveButtonTooltip>
          <MoveButtonTooltip title="Remove all">
            <Button
              sx={{ my: 0.5 }}
              variant="outlined"
              size="small"
              onClick={handleAllLeft}
              disabled={selectedItems.length === 0 || checkedItems?.length > 0}
              aria-label="move all left"
            >
              ≪
            </Button>
          </MoveButtonTooltip>
        </Grid>
      </Grid>
      <Grid item>{customList(selectedItems, "selected")}</Grid>
    </Grid>
  );
};

const MoveButtonTooltip = ({ children, title }: { children: React.ReactElement<any, any>; title: string }) => (
  <Tooltip title={<Typography variant="body1">{title}</Typography>} placement="right-start">
    {children}
  </Tooltip>
);
